import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faClipboardList,
  faFilterCircleXmark,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { SkeletonTable } from '@/components/skeletons';
import { getApiErrorMessage } from '@/lib/api-error';
import { fetchPlatformAuditLogs, type PlatformAuditLogEntry } from './api/platform-audit-log.api';
import { isKnownAuditAction, PLATFORM_AUDIT_ACTION_OPTIONS } from './audit-log-actions';
import {
  auditActorLabel,
  auditExactTimestamp,
  auditTimestampLabel,
  EMPTY_VALUE,
  platformAuditActionLabel,
  resourceIdLabel,
  toIsoRange,
} from './audit-log-presentation';

/**
 * Phase 12.7a — Platform Console → Operations → Audit Logs.
 *
 * Design of record: `docs/platform-designs/AUDIT_LOGS_DESIGN.md` **v1.0 APPROVED**
 * (D-1 … D-16 resolved 2026-08-22). Every structural choice below is a governed
 * decision, not a preference, and is annotated with the decision it implements.
 *
 * ── What this page is NOT ──────────────────────────────────────────────────
 * Read-only. **No state-changing action exists** (§6): no edit, no delete, no
 * annotate, no retention control, and **no export** (D-15). Reading the audit log
 * emits **no** audit event — recursive read-auditing is forbidden (D-16).
 * `payloadJson` is never rendered anywhere (D-13); it is excluded at the service
 * boundary and the API never returns it.
 *
 * ── Authorization ──────────────────────────────────────────────────────────
 * The backend is authoritative: `platform:audit-log:view` is enforced by
 * `PlatformPermissionGuard` on the endpoint, and direct navigation fails closed
 * with 403 regardless of what this component draws. No permission check is
 * performed here for security — the sidebar already gates visibility, and
 * `canPlatform()` is UX only.
 */

/** D-5 — the three governed page sizes. 100 is the backend maximum. */
const PAGE_SIZES = [20, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 20;

type LoadState = 'loading' | 'ready' | 'error';

export function AuditLogsPage() {
  // ── D-14 · filter and pagination state lives in the URL ──────────────────
  // A refresh preserves the view, back/forward works, and a filtered view can be
  // shared. Only the two filter values and the pagination position are encoded —
  // never an email, an actor id, or a token. A shared link grants no access: the
  // recipient still needs `platform:audit-log:view`, enforced server-side.
  const [searchParams, setSearchParams] = useSearchParams();

  const fromDate = searchParams.get('from') ?? '';
  const toDate = searchParams.get('to') ?? '';
  const rawAction = searchParams.get('action') ?? '';
  // A hand-edited query string must not inject an arbitrary action. The backend
  // would reject it anyway; dropping it here keeps the control coherent.
  const action = isKnownAuditAction(rawAction) ? rawAction : '';

  const parsedSize = Number(searchParams.get('size'));
  const pageSize = (PAGE_SIZES as readonly number[]).includes(parsedSize)
    ? parsedSize
    : DEFAULT_PAGE_SIZE;

  const parsedOffset = Number(searchParams.get('offset'));
  const offset = Number.isInteger(parsedOffset) && parsedOffset >= 0 ? parsedOffset : 0;

  const [entries, setEntries] = useState<PlatformAuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const hasFilters = fromDate !== '' || toDate !== '' || action !== '';

  const updateParams = useCallback(
    (next: Record<string, string>, resetPaging: boolean) => {
      const params = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(next)) {
        if (value === '') params.delete(key);
        else params.set(key, value);
      }
      // §5 — changing ANY filter, or the page size, returns to the first page.
      // Without this, a narrowed filter can land on an offset past the new end
      // and render an empty page that reads as "no results".
      if (resetPaging) params.delete('offset');
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    setExpandedId(null);

    const range = toIsoRange(fromDate, toDate);
    fetchPlatformAuditLogs(
      {
        limit: pageSize,
        offset,
        ...(range.from ? { from: range.from } : {}),
        ...(range.to ? { to: range.to } : {}),
        ...(action ? { action } : {}),
      },
      controller.signal,
    )
      .then((page) => {
        if (controller.signal.aborted) return;
        setEntries(page.items);
        setTotal(page.total);
        setState('ready');
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        // §5 — an outage must never render as an empty table. "No audit
        // activity" and "the server failed" are different facts and the operator
        // must be able to tell them apart (the honesty rule from 12.2B).
        setErrorMessage(getApiErrorMessage(error, 'Unable to load audit logs.'));
        setEntries([]);
        setTotal(0);
        setState('error');
      });

    return () => controller.abort();
  }, [fromDate, toDate, action, pageSize, offset, reloadToken]);

  // D-6 — the result range is derived from what was actually returned, so a
  // short final page reports its true last index.
  const rangeLabel = useMemo(() => {
    if (total === 0 || entries.length === 0) return null;
    const first = offset + 1;
    const last = offset + entries.length;
    return `Showing ${first.toLocaleString()}–${last.toLocaleString()} of ${total.toLocaleString()}`;
  }, [offset, entries.length, total]);

  const canPrev = offset > 0;
  const canNext = offset + entries.length < total;

  const clearFilters = () => updateParams({ from: '', to: '', action: '' }, true);

  return (
    <div className="space-y-5">
      {/* ── Page header — D-7 ──────────────────────────────────────────── */}
      <div>
        <h1 className="text-xl font-semibold text-foreground">Audit Logs</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Review platform activity and security events.
        </p>
      </div>

      {/* ── Filter bar — D-1, D-14. Exactly two filters. ───────────────── */}
      <section
        aria-label="Audit log filters"
        className="rounded-xl border border-border bg-card p-5"
      >
        {/* grid-cols-1 at the base width, per the 12.2H responsive constraint:
            a track floored at min-content is what caused the D-1 defect. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="min-w-0">
            <label
              htmlFor="audit-from"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              From
            </label>
            <input
              id="audit-from"
              type="date"
              value={fromDate}
              aria-label="Filter audit events from date"
              onChange={(e) => updateParams({ from: e.target.value }, true)}
              className="h-11 w-full min-w-0 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
          </div>

          <div className="min-w-0">
            <label
              htmlFor="audit-to"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              To
            </label>
            <input
              id="audit-to"
              type="date"
              value={toDate}
              aria-label="Filter audit events to date"
              onChange={(e) => updateParams({ to: e.target.value }, true)}
              className="h-11 w-full min-w-0 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
          </div>

          <div className="min-w-0">
            <label
              htmlFor="audit-action"
              className="mb-1 block text-xs font-medium text-muted-foreground"
            >
              Action
            </label>
            <select
              id="audit-action"
              value={action}
              aria-label="Filter audit events by action"
              onChange={(e) => updateParams({ action: e.target.value }, true)}
              className="h-11 w-full min-w-0 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <option value="">All actions</option>
              {PLATFORM_AUDIT_ACTION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex min-w-0 items-end">
            {/* D-9 — always reachable, not only in the no-results state, so a
                filtered view can be escaped from anywhere. */}
            <button
              type="button"
              onClick={clearFilters}
              disabled={!hasFilters}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-border px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <FontAwesomeIcon icon={faFilterCircleXmark} className="h-3.5 w-3.5" />
              Clear filters
            </button>
          </div>
        </div>
      </section>

      {/* ── Results ────────────────────────────────────────────────────── */}
      <section className="rounded-xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
          {/* aria-live so the count change is announced, not conveyed only
              visually (Accessibility §5). */}
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {state === 'loading' ? 'Loading audit events…' : (rangeLabel ?? 'No results')}
          </p>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <label htmlFor="audit-page-size">Per page</label>
            <select
              id="audit-page-size"
              aria-label="Audit events per page"
              value={pageSize}
              onChange={(e) => updateParams({ size: e.target.value }, true)}
              className="h-11 rounded-lg border border-border bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="p-5">
          {state === 'loading' ? (
            // §5 — SkeletonTable, never a bare spinner for tabular data.
            <div data-testid="audit-loading">
              <SkeletonTable rows={6} cols={5} />
            </div>
          ) : state === 'error' ? (
            <AuditMessage
              testId="audit-error"
              icon={faTriangleExclamation}
              message={errorMessage}
              onRetry={() => setReloadToken((t) => t + 1)}
            />
          ) : entries.length === 0 ? (
            // D-8 vs D-9 — two visually and semantically distinct states. An
            // outage is neither of them (handled above).
            hasFilters ? (
              <AuditMessage
                testId="audit-no-results"
                icon={faClipboardList}
                message="No audit events match your filters."
                actionLabel="Clear filters"
                onRetry={clearFilters}
              />
            ) : (
              <AuditMessage
                testId="audit-empty"
                icon={faClipboardList}
                message="No audit activity recorded yet."
              />
            )
          ) : (
            <AuditResults
              entries={entries}
              expandedId={expandedId}
              onToggle={(id) => setExpandedId((cur) => (cur === id ? null : id))}
            />
          )}
        </div>

        {/* D-6 — Previous / Next, disabled at the boundaries. */}
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <button
            type="button"
            onClick={() => updateParams({ offset: String(Math.max(0, offset - pageSize)) }, false)}
            disabled={!canPrev || state === 'loading'}
            className="inline-flex h-11 items-center rounded-lg border border-border px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => updateParams({ offset: String(offset + pageSize) }, false)}
            disabled={!canNext || state === 'loading'}
            className="inline-flex h-11 items-center rounded-lg border border-border px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            Next
          </button>
        </div>
      </section>
    </div>
  );
}

/** Shared empty / no-results / error presentation, mirroring the Dashboard's SectionMessage. */
function AuditMessage({
  icon,
  message,
  onRetry,
  actionLabel = 'Retry',
  testId,
}: {
  icon: typeof faClipboardList;
  message: string;
  onRetry?: () => void;
  actionLabel?: string;
  testId: string;
}) {
  return (
    <div
      className="flex flex-col items-center justify-center px-4 py-12 text-center"
      data-testid={testId}
      role="status"
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted">
        <FontAwesomeIcon icon={icon} className="h-4 w-4 text-muted-foreground" />
      </div>
      <p className="mt-3 max-w-sm text-sm text-muted-foreground">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex h-11 items-center rounded-lg border border-border px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

/**
 * D-2 / D-4 — one dataset, two presentations.
 *
 * Desktop keeps a real `<table>` with `<th scope="col">`. Below `md` it becomes
 * a semantic list of stacked cards, because D-4 chose the card transformation
 * over contained horizontal scrolling for this page. Both render the same five
 * data points and both reach the same expanded detail (D-3).
 */
function AuditResults({
  entries,
  expandedId,
  onToggle,
}: {
  entries: PlatformAuditLogEntry[];
  expandedId: string | null;
  onToggle: (id: string) => void;
}) {
  return (
    <>
      {/* Desktop / tablet — the real table */}
      <div className="hidden md:block">
        <table className="w-full text-left text-sm">
          <caption className="sr-only">Platform audit events, newest first</caption>
          <thead>
            <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
              <th scope="col" className="w-8 px-2 py-2" />
              <th scope="col" className="px-2 py-2 font-medium">
                Timestamp
              </th>
              <th scope="col" className="px-2 py-2 font-medium">
                Action
              </th>
              <th scope="col" className="px-2 py-2 font-medium">
                Actor
              </th>
              <th scope="col" className="px-2 py-2 font-medium">
                Resource Type
              </th>
              <th scope="col" className="px-2 py-2 font-medium">
                Resource ID
              </th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => {
              const expanded = expandedId === entry.id;
              return (
                <Fragment key={entry.id}>
                  <tr className="border-b border-border last:border-0">
                    <td className="px-2 py-3 align-top">
                      <button
                        type="button"
                        onClick={() => onToggle(entry.id)}
                        aria-expanded={expanded}
                        aria-controls={`audit-detail-${entry.id}`}
                        aria-label={`${expanded ? 'Hide' : 'Show'} details for ${platformAuditActionLabel(entry.action)}`}
                        data-testid={`audit-expand-${entry.id}`}
                        className="flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        <FontAwesomeIcon
                          icon={expanded ? faChevronDown : faChevronRight}
                          className="h-3 w-3"
                        />
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-2 py-3 align-top text-foreground">
                      {auditTimestampLabel(entry.occurredAt)}
                    </td>
                    <td className="px-2 py-3 align-top font-medium text-foreground">
                      {platformAuditActionLabel(entry.action)}
                    </td>
                    <td className="px-2 py-3 align-top text-foreground">
                      {auditActorLabel(entry)}
                    </td>
                    <td className="px-2 py-3 align-top text-muted-foreground">
                      {entry.resourceType}
                    </td>
                    <td className="max-w-[16rem] truncate px-2 py-3 align-top text-muted-foreground">
                      {resourceIdLabel(entry.resourceId)}
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="border-b border-border last:border-0">
                      <td colSpan={6} className="px-2 pb-4">
                        <AuditDetail entry={entry} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Narrow — stacked cards (D-4). Labels accompany values, since the
          column headers are gone. grid-cols-1 so nothing is floored at
          min-content and no page-level horizontal scroll can appear. */}
      <ul className="grid grid-cols-1 gap-3 md:hidden" data-testid="audit-cards">
        {entries.map((entry) => {
          const expanded = expandedId === entry.id;
          return (
            <li key={entry.id} className="min-w-0 rounded-lg border border-border p-4">
              <button
                type="button"
                onClick={() => onToggle(entry.id)}
                aria-expanded={expanded}
                aria-controls={`audit-detail-card-${entry.id}`}
                className="flex w-full min-w-0 items-start justify-between gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <span className="min-w-0">
                  {/* Action most prominent, timestamp clearly associated (D-4). */}
                  <span className="block break-words text-sm font-semibold text-foreground">
                    {platformAuditActionLabel(entry.action)}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {auditTimestampLabel(entry.occurredAt)}
                  </span>
                </span>
                <FontAwesomeIcon
                  icon={expanded ? faChevronDown : faChevronRight}
                  className="mt-1 h-3 w-3 shrink-0 text-muted-foreground"
                />
              </button>

              <dl className="mt-3 grid grid-cols-1 gap-1.5 text-xs">
                <Field label="Actor" value={auditActorLabel(entry)} />
                <Field label="Resource Type" value={entry.resourceType} />
                <Field label="Resource ID" value={resourceIdLabel(entry.resourceId)} />
              </dl>

              {expanded && (
                <div className="mt-3" id={`audit-detail-card-${entry.id}`}>
                  <AuditDetail entry={entry} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

/**
 * D-3 — the inline expanded detail.
 *
 * Every field here is already returned by the existing endpoint; none is
 * invented and none required an API change. **`payloadJson` is absent by
 * construction** — the service never returns it (D-13).
 */
function AuditDetail({ entry }: { entry: PlatformAuditLogEntry }) {
  return (
    <div
      id={`audit-detail-${entry.id}`}
      className="rounded-lg bg-muted/40 p-4"
      data-testid={`audit-detail-${entry.id}`}
    >
      <dl className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
        <Field label="Actor Role" value={entry.actorRole || EMPTY_VALUE} />
        {/* The nil UUID is shown HERE, deliberately — D-10 keeps it out of the
            Actor column as an identity, but an investigator still needs it. */}
        <Field label="Actor ID" value={entry.actorId} mono />
        <Field label="Event ID" value={entry.id} mono />
        <Field label="Exact timestamp" value={auditExactTimestamp(entry.occurredAt)} mono />
        <Field label="Resource ID (full)" value={resourceIdLabel(entry.resourceId)} mono />
      </dl>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`break-words text-foreground ${mono ? 'font-mono text-[11px]' : ''}`}>
        {value}
      </dd>
    </div>
  );
}
